import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ApiError } from '../api/client';

/**
 * UI-76: the scroller a table lives in, which also labels every cell with its column name so a
 * phone can show the row as a card. The labels are read from this table's own header, so there is
 * one source of truth and no page repeats its column names in a second place.
 */
export function DataTable({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    // UI-76: below 640px css turns these into blocks, which drops the implicit roles a browser
    // gives a table, so the roles are written down and the meaning survives the shape change
    for (const table of root.querySelectorAll('table')) table.setAttribute('role', 'table');
    for (const row of root.querySelectorAll('tr')) row.setAttribute('role', 'row');
    for (const th of root.querySelectorAll('thead th')) th.setAttribute('role', 'columnheader');
    for (const th of root.querySelectorAll('tbody th')) th.setAttribute('role', 'rowheader');
    for (const td of root.querySelectorAll('td')) td.setAttribute('role', 'cell');
    const headers = [...root.querySelectorAll('thead th')].map(
      (th) => th.textContent?.trim() ?? '',
    );
    for (const row of root.querySelectorAll('tbody tr')) {
      [...row.children].forEach((cell, i) => {
        const label = headers[i];
        // a cell with no header (the actions column) keeps no label, and the css gives it the row
        if (label) cell.setAttribute('data-label', label);
        else cell.removeAttribute('data-label');
      });
    }
  });
  return (
    <div className="table-wrap" ref={ref}>
      {children}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string | undefined;
  actions?: ReactNode;
}) {
  return (
    <div className="page-header">
      <div>
        <h1>{title}</h1>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {actions ? <div className="actions">{actions}</div> : null}
    </div>
  );
}

export function Card({
  title,
  children,
  className = '',
}: {
  title?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`card ${className}`}>
      {title ? <div className="card-title">{title}</div> : null}
      {children}
    </section>
  );
}

export function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | number;
  hint?: string;
}) {
  return (
    <div className="card stat">
      <div className="card-title">{label}</div>
      <div className="value">{value}</div>
      {hint ? <div className="hint">{hint}</div> : null}
    </div>
  );
}

const TONE: Record<string, string> = {
  active: 'badge-success',
  connected: 'badge-success',
  healthy: 'badge-success',
  sent: 'badge-success',
  read: 'badge-success',
  delivered: 'badge-success',
  posted: 'badge-success',
  completed: 'badge-success',
  valid: 'badge-success',
  open: 'badge-primary',
  paused: 'badge-warning',
  needs_reconnect: 'badge-warning',
  expiring: 'badge-warning',
  queued: 'badge-warning',
  scheduled: 'badge-warning',
  handoff: 'badge-warning',
  pending: 'badge-warning',
  throttled: 'badge-warning',
  publishing: 'badge-warning',
  failed: 'badge-danger',
  warning: 'badge-warning',
  info: 'badge-primary',
  revoked: 'badge-danger',
  disconnected: 'badge-danger',
  unhealthy: 'badge-danger',
  expired: 'badge-danger',
  error: 'badge-danger',
  draft: '',
  closed: '',
  unknown: '',
  inactive: '',
};
export function Badge({ value }: { value: string }) {
  return <span className={`badge ${TONE[value] ?? ''}`}>{value.replace(/_/g, ' ')}</span>;
}

export function ErrorAlert({ error }: { error: ApiError | Error | null | undefined }) {
  if (!error) return null;
  const e = error as Partial<ApiError> & { message: string };
  const details = e.details as
    | { path?: string; issues?: { path: string; message: string }[] }
    | undefined;
  return (
    <div className="alert alert-error" role="alert">
      <strong>{(e.code ?? 'error').split(':')[0]}</strong>: {e.message}
      {details?.path ? <span> (field: {details.path})</span> : null}
      {details?.issues ? (
        <ul style={{ margin: '6px 0 0 16px' }}>
          {details.issues.map((i) => (
            <li key={`${i.path}:${i.message}`}>
              {i.path || 'body'}: {i.message}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

/** A single control gets a wrapping label; `group` renders a labelled region for several controls. */
export function Field({
  label,
  help,
  children,
  group = false,
}: {
  label: string;
  help?: string | undefined;
  children: ReactNode;
  group?: boolean;
}) {
  const body = (
    <>
      <span className="label-text">{label}</span>
      {children}
      {help ? <span className="help">{help}</span> : null}
    </>
  );
  return group ? (
    <fieldset className="field">
      <legend className="label-text">{label}</legend>
      {children}
      {help ? <span className="help">{help}</span> : null}
    </fieldset>
  ) : (
    <label className="field">{body}</label>
  );
}

export function Modal({
  title,
  onClose,
  children,
  footer,
  wide = false,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className={`modal ${wide ? 'modal-wide' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <h2>{title}</h2>
        {children}
        {footer ? <div className="modal-footer">{footer}</div> : null}
      </div>
    </div>
  );
}

interface Toast {
  id: number;
  text: string;
  tone: 'ok' | 'error';
  /** PST-24: an optional link the text opens */
  href?: string;
}
const ToastCtx = createContext<(text: string, tone?: Toast['tone'], href?: string) => void>(
  () => {},
);
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const push = useCallback((text: string, tone: Toast['tone'] = 'ok', href?: string) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, href ? { id, text, tone, href } : { id, text, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), href ? 8000 : 3500);
  }, []);
  const value = useMemo(() => push, [push]);
  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div className="toast-host" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.tone === 'error' ? 'toast-error' : ''}`}>
            {t.href ? (
              <a href={t.href} target="_blank" rel="noreferrer">
                {t.text}
              </a>
            ) : (
              t.text
            )}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);

export const fmtDate = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString() : '—';
export const fmtPct = (n: number): string => `${(n * 100).toFixed(1)}%`;
export const relTime = (iso: string | null | undefined): string => {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
};
